import React, { useEffect, useRef } from 'react';
import SimpleBar from 'simplebar-react';
import dompurify from 'dompurify';
import { BottomSheet } from 'react-spring-bottom-sheet';

import { DEFAULT_LANG } from '../Utilities/Config';
import { isMobile } from '../Utilities/Helpers';
import Gallery from './Gallery';

import 'simplebar-react/dist/simplebar.min.css';
import 'react-spring-bottom-sheet/dist/style.css';
import './Sidebar.css';

export default function Sidebar(props) {
	const { show, data, onToggle, setSelectedData, language, setSearchParams, setSidebarWidth, categories } = props;

	const ref = useRef(null);
	const mobile = isMobile();

	// handle sidebar close btn
	const onClose = () => {
		onToggle(false);
		setSelectedData(null);
		setSearchParams(params => {
			if (params.has('id')) params.delete('id');
			return params;
		});
	}

	// generate color legend of categories
	const generateCategories = (data) => {
		const subjectIDs = [...new Set(data)];
		const langParam = (language['language:code'] !== DEFAULT_LANG) ? language['language:code'] : '';

		return subjectIDs.map((id, i) => {
			const category = categories[id];
			const postfix = langParam ? `_${langParam}` : '';
			const name = category[`name${postfix}`];
			return <div key={i}>
				<div  style={{backgroundColor: category.color}} className='category-row'>
					<div className='icon' style={{backgroundColor: category.color}}></div>
					<div className='text'>{name}</div>
				</div>
			</div>
		});
	}

	// parse logo url
	const processImages = (string) => {
		if (!string) return null;
		const gImageApiUrl = `https://lh3.googleusercontent.com/d/`;
		const urls = string.split(/[,;\s]/)
			.filter(el=>el)
			.map(rawURL => {
				let googleDriveId;
				const googleMatch = rawURL.match(/open\?id=(.*)/);
				if (googleMatch && googleMatch.length > 0) googleDriveId = googleMatch[1];
				const url = (googleDriveId) ? `${gImageApiUrl}${googleDriveId}` : rawURL;
				return url;
			});
		return urls;
	}
	

	const addSlash = (string) => {
		return (string && string.startsWith('http')) ? string : `//${string}`;
	}
	const generateContacts = (props) => {
		return [
			{class: 'website', value: props.website},
			{class: 'facebook', value: props.facebook},
			{class: 'instagram', value: props.instagram},
			{class: 'twitter', value: props.twitter},
			{class: 'linkedin', value: props.linkedin},
			{class: 'youtube', value: props.youtube}
		].map((el, i) => {
			if (!el.value) return null;
			return <a key={i} href={addSlash(el.value)} rel='noreferrer' target='_blank'>
						<div className={`icon-social icon-${el.class}`}></div>
					</a>
		}).filter(el => el);
	}

	// generate sidebar content
	const generateContent = (props, lang, mobile) => {
		if (!props) return;

		const storyId = parseInt(props.ID);
		const translatedStory = Object.keys(lang.stories).length ? lang.stories[storyId] : null;

		const pointTitle = translatedStory ? translatedStory.NAME : props.NAME;

		const summary = translatedStory ? translatedStory.description : props.description;
		const sanitizedText = dompurify.sanitize(summary, { ADD_ATTR: ['target'] });

		const logos = processImages(props.images);

		const ids = typeof(props.subjectIDs) === 'string' ? JSON.parse(props.subjectIDs) : props.subjectIDs;
		const subjects =  ids || [];
		const content = <div className='sidebar-content-wrapper'>
			<div className='sidebar-header'>
				<a href="null"> </a>
				<div className='sidebar-title'><span>{(props) ? pointTitle : null}</span></div>
				<div className='button-close' onClick={onClose}></div>
			</div>
			{logos.length && <Gallery
				images={logos}
			/>}

			<div className='sidebar-content sidebar-categories'>{generateCategories(subjects)}</div>
			<div className='sidebar-content sidebar-summary' dangerouslySetInnerHTML={{ __html: sanitizedText }} />
			<div className='sidebar-content sidebar-contacts'>{generateContacts(props)}</div>
		</div>;

		return (mobile ? content :
			<SimpleBar
				autoHide={false}
				style={{ maxHeight: '100%' }}
			>
				{content}
			</SimpleBar>
		);
	}

	// Detect Sidebar width
	useEffect(() => {
		const handleResize = () => { setSidebarWidth(ref.current ? ref.current.offsetWidth : 0) }
		handleResize();
		window.addEventListener('resize', handleResize)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ref.current]);

	return ((mobile) ?
		<BottomSheet
			onDismiss={onClose}
			open={show}
			defaultSnap={({ maxHeight }) => maxHeight / 2}
			snapPoints={({ maxHeight }) => [
				maxHeight - maxHeight / 10,
				maxHeight / 4,
				maxHeight * 0.6,
			]}
		>
			{generateContent(data, language, mobile)}
		</BottomSheet>
	:
		<div ref={ref} className={show ? 'sidebar-wrapper show': 'sidebar-wrapper'}>
			<div className='sidebar'>
				<div className='content-wrapper'>
					{generateContent(data, language, mobile)}
				</div>
			</div>
		</div>
	)
}
